import { searchItems, setSearchTerm } from 'store/SgmSearch/sgmSearchThunk';
import { redirectExternalOpenNewUrl } from 'core/services/redirectService';
import { debounce } from 'lodash-es';
import React, { useCallback, useRef, useState, FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useMatch } from 'react-router';
import { Combobox } from './Combobox';
import { QuicksearchResults } from './QuicksearchResults';
import { useLoggerTracking } from 'components/hooks/logger/useLoggerTracking';
import { useEventListener } from 'components/hooks/useEventListener';
import { useApplicationContext } from 'components/hooks/UseApplicationContext';
import { QuicksearchStartTyping } from './QuickSearchStartTyping';
import { useSearchQuery } from 'components/hooks/search/useSearchQuery';
import { useMediaBreakpoint } from 'components/hooks/media/useMediaBreakpoint';
import classNames from 'classnames';
import { useAppDispatch, useAppSelector } from 'store/store';
import { RoutePaths, SearchCategoryEnum } from 'routes/RoutePaths';
import { TrackedFeature, TrackedPage, TRACKING_PARAM_KEYS } from 'services/Logger';

import './SearchBar.scss';

const ACCEPTED_KEYS = '0123456789qwertyuiopasdfghjklzxcvbnmQWERTYUIOPASDFGHJKLZXCVBNM';

interface SearchBarProps {
    className?: string;
}

export const SearchBar: FC<SearchBarProps> = ({ className }) => {
    const { t: translate, i18n: { language } } = useTranslation('service-list');
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { canSearchPersonsAndCompanies } = useApplicationContext();
    const mediaBreakpoint = useMediaBreakpoint();
    const track = useLoggerTracking();

    const debouncedTrackActionRef = useRef(
        debounce((searchTerm: string, isSearchPage: boolean) => {
            const trackedPage = isSearchPage ? TrackedPage.AdvancedSearch : TrackedPage.QuickSearch;
            const trackedFeature = isSearchPage ? TrackedFeature.AdvancedSearch : TrackedFeature.QuickSearch;
            track(trackedPage, trackedFeature, { [TRACKING_PARAM_KEYS.searchedTerm]: searchTerm });
        }, 2000),
    );

    useEffect(() => {
        const debouncedTrackAction = debouncedTrackActionRef.current;
        return () => {
            debouncedTrackAction?.cancel();
        };
    }, []);

    const isSearchPage = !!useMatch(RoutePaths.Search.route);
    const [showQuicksearch, setShowQuicksearch] = useState(false);
    const rootElementRef = useRef(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const { selectedCategories, selectedSubcategories } = useSearchQuery();
    const { searchTerm: urlSearchTerm, searchCategory } = useMatch(RoutePaths.Search.route)?.params || {};
    const searchTerm = useAppSelector(state => state.search.searchTerm);

    useEffect(() => {
        if (urlSearchTerm && !searchTerm) {
            dispatch(setSearchTerm(urlSearchTerm));
        }
    }, [urlSearchTerm, searchTerm, dispatch]);

    const selectedCategoriesKey = selectedCategories.map(x => x.key).join(',');
    const selectedSubcategoriesKey = selectedSubcategories.map(x => x.key).join(',');

    useEffect(() => {
        dispatch(searchItems(searchTerm || urlSearchTerm || '', selectedCategories, selectedSubcategories, language));
    }, [searchTerm, urlSearchTerm, selectedCategoriesKey, selectedSubcategoriesKey, language, dispatch, selectedCategories, selectedSubcategories]);

    const keydownIntoSearch = useCallback((event: Event) => {
        const keyboardEvent = event as KeyboardEvent;
        const target = keyboardEvent.target as HTMLElement;
        const isKeyAccepted = keyboardEvent.key && ACCEPTED_KEYS.indexOf(keyboardEvent.key) > -1 && !keyboardEvent.ctrlKey;

        if (target?.tagName?.toLocaleLowerCase() === 'body'
            && isKeyAccepted
            && inputRef.current
        ) {
            inputRef.current.focus();
        }
    }, []);

    useEventListener('keydown', keydownIntoSearch);

    const updateSearchTerm = (term: string) => {
        dispatch(setSearchTerm(term));
        setShowQuicksearch(true);
        if (term) {
            debouncedTrackActionRef.current(term, isSearchPage);
        }
    };

    const onSearchTermChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newSearchTerm = event.target.value;
        updateSearchTerm(newSearchTerm);
    };

    const goToResults = (event: React.FormEvent) => {
        track(TrackedPage.QuickSearch, TrackedFeature.QuickSearch, { [TRACKING_PARAM_KEYS.searchedTerm]: searchTerm });
        const open = isSearchPage ? navigate : redirectExternalOpenNewUrl;
        open(RoutePaths.Search.url(searchTerm || '', searchCategory || SearchCategoryEnum.All));
        event.preventDefault();
    };

    const inputGroupClassName = {
        'input-group-md': ['md'].includes(mediaBreakpoint),
        'input-group-lg': ['lg', 'xl'].includes(mediaBreakpoint),
        'input-group-xl': ['xxl'].includes(mediaBreakpoint),
        'has-terms': searchTerm,
    };

    const formControlClassName = {
        'form-control-md': ['md'].includes(mediaBreakpoint),
        'form-control-lg': ['lg', 'xl', 'xxl'].includes(mediaBreakpoint),
    };

    return <form
        ref={rootElementRef}
        className={classNames('form-group w-100 search-bar', className)}
        onSubmit={goToResults}
    >
        <div className={classNames('input-group input-search', inputGroupClassName)}>
            <div className="input-icon-start">
                <em className="icon">search</em>
            </div>
            <div className="input-icon-end me-2">
                <div className={classNames({ 'd-none': !searchTerm })}>
                    <button
                        className="btn btn-flat-primary"
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            updateSearchTerm('');
                            inputRef?.current?.focus();
                        }}
                    >
                        <i className="icon icon-sm">close</i>
                    </button>
                </div>
                <div>
                    <button
                        className="btn btn-flat-primary"
                        type="submit"
                        disabled={!searchTerm}
                    >
                        {translate('service-list:search')}
                    </button>
                </div>
            </div>
            <label htmlFor="search" className="screen-reader-text">search</label>
            <input
                id="search"
                ref={inputRef}
                type="search"
                autoComplete="off"
                role="searchbox"
                className={classNames('form-control', formControlClassName)}
                placeholder={translate(
                    canSearchPersonsAndCompanies
                        ? 'service-list:searchServicesPersonAndCompaniesPlaceHolder'
                        : 'service-list:searchServicesPlaceHolder',
                )}
                value={searchTerm}
                onChange={onSearchTermChange}
                onFocus={() => setShowQuicksearch(true)}
            />
        </div>
        <Combobox
            rootRef={rootElementRef}
            isOpen={showQuicksearch && !isSearchPage}
            onOutsideClick={() => setShowQuicksearch(false)}
        >
            {searchTerm
                ? <QuicksearchResults openInNew={!isSearchPage} />
                : <QuicksearchStartTyping onCategoryClick={() => setShowQuicksearch(false)} />}
        </Combobox>
    </form>;
};
